import React, { useState, useEffect, useRef } from 'react';
import {
    Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    TablePagination, useMediaQuery, useTheme, TextField, MenuItem, InputAdornment,
    Button, Dialog, DialogTitle, DialogContent, DialogActions, Snackbar, Alert, Chip,
    Tooltip, IconButton, Switch, FormControlLabel, Autocomplete, CircularProgress, Stack
} from '@mui/material';

// Ícones
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ClearIcon from '@mui/icons-material/Clear';
import CloseIcon from '@mui/icons-material/Close';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import DrawIcon from '@mui/icons-material/Draw';
import PaymentIcon from '@mui/icons-material/Payment';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';

import api from '../services/api';
import { confirmAcao, toastSuccess } from '../services/alerts';
import { mostrarErro } from '../services/erros';
import { isVisualizador } from '../services/auth';

const FORMA_LABEL_PLANO = {
    PIX: 'PIX', DINHEIRO: 'Dinheiro',
    CARTAO_CREDITO: 'Crédito', CARTAO_DEBITO: 'Débito',
    BOLETO: 'Boleto', TRANSFERENCIA: 'Transferência',
};

// ============================================================================
// PAD DE ASSINATURA
// ============================================================================
const SignaturePad = ({ onChange, value, isMobile }) => {
    const canvasRef = useRef(null);
    const [isDrawing, setIsDrawing] = useState(false);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        ctx.lineWidth = 2;
        ctx.lineCap = 'round';
        ctx.strokeStyle = '#000';

        if (value) {
            const img = new Image();
            img.onload = () => {
                ctx.clearRect(0, 0, canvas.width, canvas.height);
                ctx.drawImage(img, 0, 0);
            };
            img.src = value;
        } else {
            ctx.clearRect(0, 0, canvas.width, canvas.height);
        }
    }, [value]);

    const getCoordinates = (e, canvas) => {
        const rect = canvas.getBoundingClientRect();
        const clientX = e.clientX || (e.touches && e.touches[0].clientX);
        const clientY = e.clientY || (e.touches && e.touches[0].clientY);
        return {
            x: clientX - rect.left,
            y: clientY - rect.top
        };
    };

    const startDrawing = (e) => {
        setIsDrawing(true);
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        const coords = getCoordinates(e, canvas);
        ctx.beginPath();
        ctx.moveTo(coords.x, coords.y);
        e.preventDefault();
    };

    const draw = (e) => {
        if (!isDrawing) return;
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        const coords = getCoordinates(e, canvas);
        ctx.lineTo(coords.x, coords.y);
        ctx.stroke();
        if(e.cancelable) e.preventDefault();
    };

    const stopDrawing = () => {
        if (!isDrawing) return;
        setIsDrawing(false);
        const canvas = canvasRef.current;
        const dataURL = canvas.toDataURL('image/png');
        onChange(dataURL);
    };

    const clearSignature = () => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        onChange('');
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
            <Box
                sx={{
                    border: '2px dashed #ccc',
                    borderRadius: 2,
                    bgcolor: '#fafafa',
                    touchAction: 'none'
                }}
            >
                <canvas
                    ref={canvasRef}
                    width={isMobile ? 300 : 400} // Ligeiramente menor no mobile
                    height={200}
                    style={{ cursor: 'crosshair', display: 'block' }}
                    onMouseDown={startDrawing}
                    onMouseMove={draw}
                    onMouseUp={stopDrawing}
                    onMouseLeave={stopDrawing}
                    onTouchStart={startDrawing}
                    onTouchMove={draw}
                    onTouchEnd={stopDrawing}
                />
            </Box>
            <Button
                variant="outlined"
                color="error"
                startIcon={<ClearIcon />}
                onClick={clearSignature}
                size="small"
            >
                Limpar Assinatura
            </Button>
        </Box>
    );
};


export default function AssinaturaPaciente() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const soLeitura = isVisualizador();

    const [assinaturas, setAssinaturas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openDialog, setOpenDialog] = useState(false);
    const [editingAssinatura, setEditingAssinatura] = useState(null);
    const [avisoAssinaturaAtiva, setAvisoAssinaturaAtiva] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const [pacienteSelecionado, setPacienteSelecionado] = useState(null);
    const [psicologos, setPsicologos] = useState([]);
    const [pacientes, setPacientes] = useState([]);
    const [planos, setPlanos] = useState([]);
    const [periodicidades, setPeriodicidades] = useState([]);
    const [planosPagamento, setPlanosPagamento] = useState([]);
    const [renovacoesPendentes, setRenovacoesPendentes] = useState([]);
    const [uploadDialog, setUploadDialog] = useState({ open: false, assinatura: null });
    const [uploadFile, setUploadFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [gerandoTermo, setGerandoTermo] = useState(null);
    const uploadInputRef = useRef(null);

    const initialState = {
        idPaciente: '',
        idPsicologo: '',
        id_P_Atendimento: '',
        idPeriodicidade: '',
        dataInicio: '',
        dataFim: '',
        valorCobrado: '',
        sessoesUtilizadas: 0,
        sessoesRestantes: 0,
        renovacaoAutomatica: false,
        diaPagamento: '',
        idPlanoPagamento: '',
        observacoes: '',
        assinaturaDigital: '',
        statusAssinatura: 'AGUARDANDO_ASSINATURA',
        tipoAssinatura: 'MANUAL',
    };
    const [formData, setFormData] = useState(initialState);

    const [filtroPaciente, setFiltroPaciente] = useState('');
    const [filtroStatus, setFiltroStatus] = useState('Todos');

    // Paginacao server-side
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalElements, setTotalElements] = useState(0);

    useEffect(() => {
        carregarDadosAuxiliares();
    }, []);

    // Debounce do filtro de paciente
    const [filtroPacienteDebounced, setFiltroPacienteDebounced] = useState('');
    useEffect(() => {
        const t = setTimeout(() => setFiltroPacienteDebounced(filtroPaciente), 400);
        return () => clearTimeout(t);
    }, [filtroPaciente]);

    useEffect(() => {
        carregarAssinaturas();
    }, [page, rowsPerPage, filtroPacienteDebounced, filtroStatus]);

    const carregarAssinaturas = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({ page, size: rowsPerPage });
            if (filtroPacienteDebounced) params.append('nomePaciente', filtroPacienteDebounced);
            if (filtroStatus && filtroStatus !== 'Todos') params.append('status', filtroStatus);
            const res = await api.get(`/assinaturas-paciente/paginado?${params.toString()}`);
            const data = res.data;
            setAssinaturas(Array.isArray(data) ? data : (data?.content ?? []));
            setTotalElements(data?.totalElements ?? (Array.isArray(data) ? data.length : 0));
        } catch (error) {
            console.error('Erro ao carregar assinaturas:', error);
            mostrarMensagem('Erro ao carregar assinaturas.', 'error');
        } finally {
            setLoading(false);
        }
    };

    const carregarDadosAuxiliares = async () => {
        try {
            // O campo Paciente e uma lista suspensa, entao a lista vem pronta.
            // `/pacientes/busca` devolve so id e nome (nao o cadastro inteiro).
            const [resPacientes, resPsicologos, resPlanos, resPeriodicidades, resPlanosPagamento, resRenovacoes] = await Promise.all([
                api.get('/pacientes/busca', { params: { limit: 500 } }),
                api.get('/psicologos'),
                api.get('/planos-atendimento'),
                api.get('/periodicidades'),
                api.get('/planos-pagamento?ativo=true'),
                api.get('/renovacao-assinatura/pendentes').catch(() => ({ data: [] }))
            ]);
            const ta = (d) => Array.isArray(d) ? d : (d?.content ?? []);
            setPacientes(ta(resPacientes.data));
            setPsicologos(ta(resPsicologos.data));
            setPlanos(ta(resPlanos.data));
            setPeriodicidades(ta(resPeriodicidades.data));
            setPlanosPagamento(ta(resPlanosPagamento.data));
            setRenovacoesPendentes(ta(resRenovacoes.data));
        } catch (error) {
            console.error('Erro ao carregar dados auxiliares:', error);
            mostrarMensagem('Erro ao carregar dados. Verifique os endpoints da API.', 'error');
        }
    };

    const carregarDadosIniciais = async () => {
        await Promise.all([carregarAssinaturas(), carregarDadosAuxiliares()]);
    };

    const handleOpenDialog = (assinatura = null) => {
        if (assinatura) {
            setEditingAssinatura(assinatura);
            // O campo de busca não tem mais a lista toda para resolver o id em nome:
            // o paciente da assinatura é semeado a partir do próprio registro.
            setPacienteSelecionado(assinatura.idPaciente
                ? { id_paciente: assinatura.idPaciente, nomeCompleto: assinatura.nomePaciente || '' }
                : null);
            setFormData({
                idPaciente: assinatura.idPaciente || '',
                idPsicologo: assinatura.idPsicologo || '',
                id_P_Atendimento: assinatura.idPlanoAtendimento || '',
                idPeriodicidade: assinatura.idPeriodicidade || '',
                dataInicio: assinatura.dataInicio ? assinatura.dataInicio.substring(0, 10) : '',
                dataFim: assinatura.dataFim ? assinatura.dataFim.substring(0, 10) : '',
                valorCobrado: assinatura.valorCobrado || '',
                sessoesUtilizadas: assinatura.sessoesUtilizadas || 0,
                sessoesRestantes: assinatura.sessoesRestantes || 0,
                renovacaoAutomatica: assinatura.renovacaoAutomatica || false,
                diaPagamento: assinatura.diaPagamento || '',
                idPlanoPagamento: assinatura.idPlanoPagamento || '',
                observacoes: assinatura.observacoes || '',
                assinaturaDigital: assinatura.assinaturaDigital || '',
                statusAssinatura: assinatura.statusAssinatura || 'ATIVA',
                tipoAssinatura: assinatura.tipoAssinatura || 'MANUAL',
            });
        } else {
            setEditingAssinatura(null);
            setPacienteSelecionado(null);
            setFormData(initialState);
        }
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setEditingAssinatura(null);
        setPacienteSelecionado(null);
        setAvisoAssinaturaAtiva(false);
    };

    const handleSave = async () => {
        if (!formData.idPaciente || !formData.id_P_Atendimento || !formData.valorCobrado || !formData.dataInicio) {
            mostrarMensagem('Preencha os campos obrigatórios: Paciente, Plano, Valor e Data de Início.', 'warning');
            return;
        }
        if (avisoAssinaturaAtiva && !editingAssinatura) {
            mostrarMensagem('Este paciente já possui uma assinatura ativa. Cancele a atual antes de criar uma nova.', 'error');
            return;
        }

        try {
            const dataToSend = {
                ...formData,
                valorCobrado: parseFloat(formData.valorCobrado),
                sessoesUtilizadas: parseInt(formData.sessoesUtilizadas, 10),
                sessoesRestantes: parseInt(formData.sessoesRestantes, 10),
                idPeriodicidade: formData.idPeriodicidade || null,
                idPlanoPagamento: formData.idPlanoPagamento || null,
                diaPagamento: formData.diaPagamento ? parseInt(formData.diaPagamento, 10) : null,
            };

            if (editingAssinatura) {
                await api.put(`/assinaturas-paciente/${editingAssinatura.id}`, dataToSend);
                mostrarMensagem('Assinatura atualizada com sucesso!');
            } else {
                await api.post('/assinaturas-paciente', dataToSend);
                mostrarMensagem('Assinatura criada com sucesso!');
            }
            carregarDadosIniciais();
            handleCloseDialog();
        } catch (error) {
            console.error('Erro ao salvar assinatura:', error);
            const errorMsg = error.response?.data?.message || 'Erro ao salvar assinatura.';
            mostrarMensagem(errorMsg, 'error');
        }
    };

    const handleDelete = async (id) => {
        const ok = await confirmAcao({
            title: 'Excluir assinatura?',
            text: 'Os títulos e o saldo de sessões vinculados a ela deixam de ter origem. Esta ação não pode ser desfeita.',
            confirmButtonText: 'Excluir',
            icon: 'warning',
            danger: true,
        });
        if (!ok) return;

        try {
            await api.delete(`/assinaturas-paciente/${id}`, { skipGlobalError: true });
            toastSuccess('Assinatura excluída.');
            carregarDadosIniciais();
        } catch (error) {
            mostrarErro(error, 'excluir a assinatura');
        }
    };

    const mostrarMensagem = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

    const handleGerarRelatorioManual = async (item) => {
        try {
            const response = await api.get(`/assinaturas-paciente/${item.id}/relatorio-manual`, { responseType: 'blob' });
            const url = URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
            const link = document.createElement('a');
            link.href = url;
            link.download = `contrato_${item.nomePaciente?.replace(/\s+/g, '_')}.pdf`;
            link.click();
            URL.revokeObjectURL(url);
        } catch {
            mostrarMensagem('Erro ao gerar relatório.', 'error');
        }
    };

    const handleGerarTermoDigital = async (item) => {
        setGerandoTermo(item.id);
        try {
            await api.post(`/assinaturas-paciente/${item.id}/gerar-termo-digital`);
            mostrarMensagem('Termo de aceite gerado e salvo com sucesso!');
            carregarDadosIniciais();
        } catch (error) {
            const msg = error.response?.data?.message || 'Erro ao gerar termo digital.';
            mostrarMensagem(msg, 'error');
        } finally {
            setGerandoTermo(null);
        }
    };

    const handleUploadDocumento = async () => {
        if (!uploadFile) return;
        setUploading(true);
        const formData = new FormData();
        formData.append('arquivo', uploadFile);
        try {
            await api.post(`/assinaturas-paciente/${uploadDialog.assinatura.id}/upload-documento-assinado`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            mostrarMensagem('Documento assinado enviado com sucesso!');
            setUploadDialog({ open: false, assinatura: null });
            setUploadFile(null);
            carregarDadosIniciais();
        } catch {
            mostrarMensagem('Erro ao enviar documento.', 'error');
        } finally {
            setUploading(false);
        }
    };

    const handleVerDocumento = async (item, tipo) => {
        try {
            const response = await api.get(`/assinaturas-paciente/${item.id}/url-download?tipo=${tipo}`);
            window.open(response.data.url, '_blank');
        } catch {
            mostrarMensagem('Erro ao obter link do documento.', 'error');
        }
    };

    const getStatusChip = (status) => {
        switch (status) {
            case 'ATIVA': return <Chip label="Ativa" color="success" size="small" />;
            case 'ENCERRADA': return <Chip label="Encerrada" color="default" size="small" />;
            case 'CANCELADA': return <Chip label="Cancelada" color="error" size="small" />;
            case 'SUSPENSA': return <Chip label="Suspensa" color="warning" size="small" />;
            case 'AGUARDANDO_PAGAMENTO': return <Chip label="Ag. Pagamento" color="info" size="small" />;
            case 'AGUARDANDO_ASSINATURA': return <Chip label="Ag. Assinatura" color="info" size="small" variant="outlined" />;
            default: return <Chip label={status || 'N/A'} size="small" />;
        }
    };

    // Filtros aplicados server-side, lista ja vem filtrada
    const filteredData = assinaturas;

    // Na edicao, o paciente da assinatura pode estar fora da lista carregada
    // (cadastro antigo, ou alem do limite da consulta). Sem ele entre as
    // opcoes o Select mostraria o campo vazio e apagaria o vinculo ao salvar.
    const opcoesPacientes = (() => {
        const base = [...pacientes].sort((a, b) => (a.nomeCompleto || '').localeCompare(b.nomeCompleto || '', 'pt-BR'));
        if (pacienteSelecionado && !base.some(p => p.id_paciente === pacienteSelecionado.id_paciente)) {
            return [pacienteSelecionado, ...base];
        }
        return base;
    })();

    if (loading) {
        return <Typography sx={{ p: 3 }}>Carregando assinaturas...</Typography>;
    }

    return (
        <Box sx={{ flexGrow: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant={isMobile ? 'h5' : 'h4'} component="div" sx={{ fontWeight: 'bold' }}>
                    Assinaturas de Pacientes
                </Typography>
                {!soLeitura && (
                    <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
                        Nova Assinatura
                    </Button>
                )}
            </Box>

            <Paper elevation={0} sx={{ p: isMobile ? 2 : 3, borderRadius: 2 }}>
                <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
                    <TextField
                        placeholder="Pesquisar por paciente..."
                        variant="outlined"
                        fullWidth
                        value={filtroPaciente}
                        onChange={(e) => { setFiltroPaciente(e.target.value); setPage(0); }}
                        InputProps={{
                            startAdornment: <InputAdornment position="start"><SearchIcon color="action" /></InputAdornment>,
                        }}
                    />
                    <TextField select label="Status" value={filtroStatus} onChange={(e) => { setFiltroStatus(e.target.value); setPage(0); }} sx={{ minWidth: 150 }}>
                        <MenuItem value="Todos">Todos</MenuItem>
                        <MenuItem value="ATIVA">Ativa</MenuItem>
                        <MenuItem value="ENCERRADA">Encerrada</MenuItem>
                        <MenuItem value="CANCELADA">Cancelada</MenuItem>
                        <MenuItem value="SUSPENSA">Suspensa</MenuItem>
                        <MenuItem value="AGUARDANDO_PAGAMENTO">Ag. Pagamento</MenuItem>
                        <MenuItem value="AGUARDANDO_ASSINATURA">Ag. Assinatura</MenuItem>
                    </TextField>
                </Box>

                <TableContainer sx={{ overflowX: 'auto' }}>
                    <Table size={isMobile ? 'small' : 'medium'} sx={{ tableLayout: 'auto', minWidth: isMobile ? 'unset' : 720 }}>
                        <TableHead>
                            <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                                <TableCell sx={{ whiteSpace: 'nowrap' }}><strong>Paciente</strong></TableCell>
                                <TableCell sx={{ display: isMobile ? 'none' : 'table-cell', whiteSpace: 'nowrap' }}><strong>Plano</strong></TableCell>
                                <TableCell sx={{ whiteSpace: 'nowrap' }}><strong>Valor</strong></TableCell>
                                <TableCell sx={{ display: isMobile ? 'none' : 'table-cell', whiteSpace: 'nowrap' }}><strong>Início</strong></TableCell>
                                <TableCell sx={{ whiteSpace: 'nowrap' }}><strong>Status</strong></TableCell>
                                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}><strong>Ações</strong></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredData.length > 0 ? filteredData.map((item) => (
                                <TableRow key={item.id} hover>
                                    <TableCell sx={{ fontWeight: 500, maxWidth: 220 }}>
                                        <Tooltip title={item.nomePaciente || 'N/A'} placement="top">
                                            <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
                                                {item.nomePaciente || 'N/A'}
                                            </Typography>
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell sx={{ display: isMobile ? 'none' : 'table-cell', maxWidth: 180 }}>
                                        <Tooltip title={item.nomePlanoAtendimento || 'N/A'} placement="top">
                                            <Typography variant="body2" noWrap>{item.nomePlanoAtendimento || 'N/A'}</Typography>
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>R$ {Number(item.valorCobrado).toFixed(2)}</TableCell>
                                    <TableCell sx={{ display: isMobile ? 'none' : 'table-cell', whiteSpace: 'nowrap' }}>
                                        {item.dataInicio ? new Date(item.dataInicio).toLocaleDateString('pt-BR', { timeZone: 'UTC' }) : '-'}
                                    </TableCell>
                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{getStatusChip(item.statusAssinatura)}</TableCell>
                                    <TableCell align="right" sx={{ whiteSpace: 'nowrap', minWidth: 200 }}>
                                        <Stack direction="row" spacing={0.5} justifyContent="flex-end" sx={{ flexWrap: 'nowrap' }}>
                                        {/* WhatsApp renovação */}
                                        {(() => {
                                            const renovacao = renovacoesPendentes.find(r => r.idAssinatura === item.id);
                                            if (renovacao) {
                                                const url = `${window.location.origin}/renovacao/${renovacao.token}`;
                                                const msg = encodeURIComponent(`Olá, ${item.nomePaciente}! Sua assinatura do plano "${item.nomePlanoAtendimento}" vence em breve. Acesse o link para renovar: ${url}`);
                                                return (
                                                    <Tooltip title="Copiar link de renovação via WhatsApp">
                                                        <IconButton size="small" sx={{ color: '#25D366' }} onClick={() => window.open(`https://wa.me/?text=${msg}`, '_blank')}>
                                                            <WhatsAppIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                );
                                            }
                                            return null;
                                        })()}

                                        {/* Ações de documento por tipo */}
                                        {item.tipoAssinatura === 'MANUAL' && (
                                            <>
                                                <Tooltip title="Baixar contrato em PDF (para assinatura física)">
                                                    <IconButton size="small" color="primary" onClick={() => handleGerarRelatorioManual(item)}>
                                                        <PictureAsPdfIcon />
                                                    </IconButton>
                                                </Tooltip>
                                                {item.urlDocumentoAssinado ? (
                                                    <Tooltip title="Ver documento assinado">
                                                        <IconButton size="small" sx={{ color: 'success.main' }} onClick={() => handleVerDocumento(item, 'documento')}>
                                                            <TaskAltIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                ) : !soLeitura && (
                                                    <Tooltip title="Enviar documento assinado">
                                                        <IconButton size="small" color="secondary" onClick={() => setUploadDialog({ open: true, assinatura: item })}>
                                                            <UploadFileIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </>
                                        )}

                                        {item.tipoAssinatura === 'DIGITAL' && (
                                            <>
                                                {item.urlTermoAceite ? (
                                                    <Tooltip title="Ver termo de aceite eletrônico (envie via WhatsApp)">
                                                        <IconButton size="small" sx={{ color: 'success.main' }} onClick={() => handleVerDocumento(item, 'termo')}>
                                                            <TaskAltIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                ) : !soLeitura && (
                                                    <Tooltip title="Gerar termo de aceite eletrônico">
                                                        <span>
                                                            <IconButton size="small" color="primary" onClick={() => handleGerarTermoDigital(item)} disabled={gerandoTermo === item.id}>
                                                                {gerandoTermo === item.id ? <CircularProgress size={16} /> : <DrawIcon />}
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                )}
                                                {/* Permite anexar PDF assinado tb em DIGITAL */}
                                                {item.urlDocumentoAssinado ? (
                                                    <Tooltip title="Ver PDF assinado pelo paciente">
                                                        <IconButton size="small" sx={{ color: 'success.dark' }} onClick={() => handleVerDocumento(item, 'documento')}>
                                                            <PictureAsPdfIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                ) : !soLeitura && (
                                                    <Tooltip title="Anexar PDF assinado pelo paciente">
                                                        <IconButton size="small" color="secondary" onClick={() => setUploadDialog({ open: true, assinatura: item })}>
                                                            <UploadFileIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </>
                                        )}

                                        {!soLeitura && (
                                            <>
                                                <Tooltip title="Editar">
                                                    <IconButton onClick={() => handleOpenDialog(item)} size="small" color="primary"><EditIcon /></IconButton>
                                                </Tooltip>
                                                <Tooltip title="Excluir">
                                                    <IconButton onClick={() => handleDelete(item.id)} size="small" color="error"><DeleteIcon /></IconButton>
                                                </Tooltip>
                                            </>
                                        )}
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                            )) : (
                                <TableRow>
                                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                                        <Typography color="text.secondary">Nenhuma assinatura encontrada.</Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                <TablePagination
                    component="div"
                    count={totalElements}
                    page={page}
                    onPageChange={(_, novaPagina) => setPage(novaPagina)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => {
                        setRowsPerPage(parseInt(e.target.value, 10));
                        setPage(0);
                    }}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    labelRowsPerPage="Linhas por pagina:"
                    labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                />
            </Paper>

            {/* MODAL (tela cheia no telemóvel) */}
            <Dialog
                open={openDialog}
                onClose={handleCloseDialog}
                maxWidth="md"
                fullWidth
                fullScreen={isMobile}
            >
                <DialogTitle
                    sx={{
                        fontWeight: 'bold',
                        bgcolor: '#f8f9fa',
                        borderBottom: '1px solid #eee',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        p: isMobile ? 2 : 3
                    }}
                >
                    <Box>
                        {editingAssinatura ? 'Editar Assinatura' : 'Nova Assinatura de Paciente'}
                    </Box>
                    <IconButton onClick={handleCloseDialog} edge="end">
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent sx={{ pt: 3 }}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: 3, pt: 1 }}>
                        <Box sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}>
                            <TextField
                                select
                                fullWidth
                                required
                                label="Paciente"
                                name="idPaciente"
                                value={formData.idPaciente || ''}
                                onChange={(e) => {
                                    const idSelecionado = e.target.value;
                                    const temAtiva = !!idSelecionado && assinaturas.some(
                                        a => a.idPaciente === idSelecionado && a.statusAssinatura === 'ATIVA'
                                    );
                                    setAvisoAssinaturaAtiva(temAtiva && !editingAssinatura);
                                    setPacienteSelecionado(opcoesPacientes.find(p => p.id_paciente === idSelecionado) || null);
                                    setFormData({ ...formData, idPaciente: idSelecionado });
                                }}
                                disabled={soLeitura}
                                SelectProps={{ MenuProps: { PaperProps: { sx: { maxHeight: 320 } } } }}
                            >
                                {opcoesPacientes.length === 0 && (
                                    <MenuItem value="" disabled>Nenhum paciente cadastrado</MenuItem>
                                )}
                                {opcoesPacientes.map((pac) => (
                                    <MenuItem key={pac.id_paciente} value={pac.id_paciente}>
                                        {pac.nomeCompleto}
                                    </MenuItem>
                                ))}
                            </TextField>
                            {avisoAssinaturaAtiva && (
                                <Alert severity="warning" sx={{ mt: 1 }}>
                                    Este paciente já possui uma assinatura ativa. Cancele a atual antes de criar uma nova.
                                </Alert>
                            )}
                        </Box>
                        <Box sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}>
                            <Autocomplete
                                options={planos}
                                getOptionLabel={(option) => option.nome || ''}
                                value={planos.find(p => p.id_p_atendimento === formData.id_P_Atendimento) || null}
                                onChange={(event, newValue) => {
                                    setFormData(prev => ({
                                        ...prev,
                                        id_P_Atendimento: newValue ? newValue.id_p_atendimento : '',
                                        valorCobrado: newValue ? (newValue.valorTotal || newValue.valorSessaoAvulsa || prev.valorCobrado) : prev.valorCobrado
                                    }));
                                }}
                                isOptionEqualToValue={(option, value) => option.id_p_atendimento === value.id_p_atendimento}
                                disabled={soLeitura}
                                renderInput={(params) => <TextField {...params} label="Plano de Atendimento" required fullWidth />}
                            />
                        </Box>

                        <Box sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}>
                            <Autocomplete
                                options={psicologos}
                                getOptionLabel={(option) => option.nomeCompleto || ''}
                                value={psicologos.find(p => p.id_psicologo === formData.idPsicologo) || null}
                                onChange={(event, newValue) => {
                                    setFormData({ ...formData, idPsicologo: newValue ? newValue.id_psicologo : '' });
                                }}
                                isOptionEqualToValue={(option, value) => option.id_psicologo === value.id_psicologo}
                                disabled={soLeitura}
                                renderInput={(params) => <TextField {...params} label="Psicólogo Responsável" fullWidth />}
                            />
                        </Box>
                        <Box sx={{ gridColumn: { xs: 'span 12', sm: 'span 3' } }}>
                            <TextField fullWidth required label="Valor Cobrado" type="number" name="valorCobrado" value={formData.valorCobrado} onChange={(e) => setFormData({ ...formData, valorCobrado: e.target.value })} InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }} disabled={soLeitura} />
                        </Box>
                        <Box sx={{ gridColumn: { xs: 'span 12', sm: 'span 3' } }}>
                            <TextField select fullWidth label="Status" name="statusAssinatura" value={formData.statusAssinatura} onChange={(e) => setFormData({ ...formData, statusAssinatura: e.target.value })} disabled={soLeitura}>
                                <MenuItem value="ATIVA">Ativa</MenuItem>
                                <MenuItem value="ENCERRADA">Encerrada</MenuItem>
                                <MenuItem value="CANCELADA">Cancelada</MenuItem>
                                <MenuItem value="SUSPENSA">Suspensa</MenuItem>
                                <MenuItem value="AGUARDANDO_PAGAMENTO">Ag. Pagamento</MenuItem>
                        <MenuItem value="AGUARDANDO_ASSINATURA">Ag. Assinatura</MenuItem>
                            </TextField>
                        </Box>

                        <Box sx={{ gridColumn: { xs: 'span 12', sm: 'span 4' } }}>
                            <TextField fullWidth required label="Data de Início" type="date" name="dataInicio" value={formData.dataInicio} onChange={(e) => setFormData({ ...formData, dataInicio: e.target.value })} InputLabelProps={{ shrink: true }} disabled={soLeitura} />
                        </Box>
                        <Box sx={{ gridColumn: { xs: 'span 12', sm: 'span 4' } }}>
                            <TextField fullWidth label="Data de Fim" type="date" name="dataFim" value={formData.dataFim} onChange={(e) => setFormData({ ...formData, dataFim: e.target.value })} InputLabelProps={{ shrink: true }} helperText="Deixe em branco para indet." disabled={soLeitura} />
                        </Box>
                        <Box sx={{ gridColumn: { xs: 'span 12', sm: 'span 4' } }}>
                            <TextField select fullWidth label="Tipo" name="tipoAssinatura" value={formData.tipoAssinatura} onChange={(e) => setFormData({ ...formData, tipoAssinatura: e.target.value })} disabled={soLeitura}>
                                <MenuItem value="MANUAL">Manual</MenuItem>
                                <MenuItem value="DIGITAL">Digital</MenuItem>
                            </TextField>
                        </Box>

                        <Box sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}>
                            <Autocomplete
                                options={periodicidades}
                                getOptionLabel={(option) => option.nome || ''}
                                value={periodicidades.find(p => p.id_periodicidade === formData.idPeriodicidade) || null}
                                onChange={(event, newValue) => setFormData({ ...formData, idPeriodicidade: newValue ? newValue.id_periodicidade : '' })}
                                isOptionEqualToValue={(option, value) => option.id_periodicidade === value.id_periodicidade}
                                disabled={soLeitura}
                                renderInput={(params) => <TextField {...params} label="Periodicidade" fullWidth />}
                            />
                        </Box>

                        <Box sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}>
                            <Autocomplete
                                options={planosPagamento}
                                getOptionLabel={(option) => option.nome || ''}
                                value={planosPagamento.find(p => p.id_p_pagamento === formData.idPlanoPagamento) || null}
                                onChange={(event, newValue) => setFormData({ ...formData, idPlanoPagamento: newValue ? newValue.id_p_pagamento : '' })}
                                isOptionEqualToValue={(option, value) => option.id_p_pagamento === value.id_p_pagamento}
                                disabled={soLeitura}
                                renderOption={(props, option) => {
                                    const formas = Array.isArray(option.formasPagamento) ? option.formasPagamento : (option.formaPagamento ? [option.formaPagamento] : []);
                                    return (
                                        <li {...props} key={option.id_p_pagamento} style={{ alignItems: 'flex-start', paddingTop: 8, paddingBottom: 8 }}>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.4, width: '100%' }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                    <PaymentIcon fontSize="small" sx={{ color: 'primary.main' }} />
                                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{option.nome}</Typography>
                                                    {formas.map(f => (
                                                        <Chip key={f} size="small" label={FORMA_LABEL_PLANO[f] || f} sx={{ height: 20, fontSize: 11 }} />
                                                    ))}
                                                </Box>
                                                {option.descricaoContaBancaria && (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, pl: 0.3 }}>
                                                        <AccountBalanceIcon sx={{ fontSize: 14, color: '#888' }} />
                                                        <Typography variant="caption" sx={{ color: '#666' }}>{option.descricaoContaBancaria}</Typography>
                                                    </Box>
                                                )}
                                            </Box>
                                        </li>
                                    );
                                }}
                                filterOptions={(opts, state) => {
                                    const q = state.inputValue.toLowerCase();
                                    if (!q) return opts;
                                    return opts.filter(o => {
                                        const formas = Array.isArray(o.formasPagamento) ? o.formasPagamento : [];
                                        return (o.nome || '').toLowerCase().includes(q)
                                            || (o.descricaoContaBancaria || '').toLowerCase().includes(q)
                                            || formas.some(f => (FORMA_LABEL_PLANO[f] || f).toLowerCase().includes(q));
                                    });
                                }}
                                renderInput={(params) => <TextField {...params} label="Plano de Pagamento" fullWidth />}
                                slotProps={{ listbox: { sx: { maxHeight: 320 } } }}
                            />
                        </Box>

                        <Box sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' }, display: 'flex', alignItems: 'center', minHeight: 56 }}>
                            <FormControlLabel
                                control={<Switch checked={formData.renovacaoAutomatica} onChange={(e) => setFormData({ ...formData, renovacaoAutomatica: e.target.checked })} name="renovacaoAutomatica" color="primary" disabled={soLeitura} />}
                                label="Renovação Automática"
                            />
                        </Box>

                        {formData.renovacaoAutomatica && (
                            <Box sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}>
                                <TextField
                                    fullWidth
                                    label="Dia de Pagamento"
                                    type="number"
                                    inputProps={{ min: 1, max: 31 }}
                                    value={formData.diaPagamento}
                                    onChange={(e) => setFormData({ ...formData, diaPagamento: e.target.value })}
                                    helperText="Dia do mês para vencimento das parcelas (1–31)"
                                    disabled={soLeitura}
                                />
                            </Box>
                        )}

                        <Box sx={{ gridColumn: 'span 12' }}>
                            <TextField fullWidth label="Observações" multiline rows={3} name="observacoes" value={formData.observacoes} onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })} disabled={soLeitura} />
                        </Box>

                        {/* INFORMATIVO MANUAL */}
                        {formData.tipoAssinatura === 'MANUAL' && (
                            <Box sx={{ gridColumn: 'span 12' }}>
                                <Alert severity="info" icon={<PictureAsPdfIcon />}>
                                    Após salvar, use o botão <strong>PDF</strong> na linha da tabela para baixar o contrato em branco. Depois, envie o documento físico assinado via <strong>Upload</strong>.
                                </Alert>
                            </Box>
                        )}

                        {/* INFORMATIVO DIGITAL */}
                        {formData.tipoAssinatura === 'DIGITAL' && (
                            <Box sx={{ gridColumn: 'span 12' }}>
                                <Alert severity="success" icon={<DrawIcon />}>
                                    Ao salvar, o <strong>Termo de Aceite Eletrônico</strong> é gerado automaticamente em PDF.
                                    Envie ao paciente via <strong>WhatsApp</strong> usando o botão na linha da tabela.
                                    Se o paciente devolver o PDF assinado, anexe pelo botão <strong>Upload</strong>.
                                </Alert>
                            </Box>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 2, bgcolor: '#f8f9fa', borderTop: '1px solid #eee' }}>
                    <Button onClick={handleCloseDialog} color="inherit">{soLeitura ? 'Fechar' : 'Cancelar'}</Button>
                    {!soLeitura && (
                        <Button onClick={handleSave} variant="contained" fullWidth={isMobile}>
                            {editingAssinatura ? 'Salvar Alterações' : 'Criar Assinatura'}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>

            {/* DIALOG UPLOAD DOCUMENTO ASSINADO */}
            <Dialog open={uploadDialog.open} onClose={() => { setUploadDialog({ open: false, assinatura: null }); setUploadFile(null); }} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontWeight: 'bold' }}>
                    Enviar Documento Assinado
                    <Typography variant="body2" color="text.secondary" fontWeight="normal">
                        {uploadDialog.assinatura?.nomePaciente}
                    </Typography>
                </DialogTitle>
                <DialogContent sx={{ pt: 2 }}>
                    <Alert severity="info" sx={{ mb: 2 }}>
                        Envie o contrato físico digitalizado após a coleta da assinatura do paciente.
                    </Alert>
                    <input
                        ref={uploadInputRef}
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png"
                        style={{ display: 'none' }}
                        onChange={(e) => setUploadFile(e.target.files[0])}
                    />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Button variant="outlined" startIcon={<UploadFileIcon />} onClick={() => uploadInputRef.current?.click()}>
                            Selecionar arquivo
                        </Button>
                        <Typography variant="body2" color={uploadFile ? 'text.primary' : 'text.secondary'}>
                            {uploadFile ? uploadFile.name : 'Nenhum arquivo selecionado'}
                        </Typography>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => { setUploadDialog({ open: false, assinatura: null }); setUploadFile(null); }} color="inherit">Cancelar</Button>
                    <Button onClick={handleUploadDocumento} variant="contained" disabled={!uploadFile || uploading} startIcon={uploading ? <CircularProgress size={16} /> : <UploadFileIcon />}>
                        {uploading ? 'Enviando...' : 'Enviar'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}

